import { useEffect, useState } from 'react'
import { useTaskStore } from '@/stores/useTaskStore'
import { useAgentStore } from '@/stores/useAgentStore'
import { apiClient } from '@/lib/api-client'
import type { Task } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

export default function TasksPage() {
  const { tasks, filters, setTasks, setFilters, setCurrentTask } = useTaskStore()
  const { agent } = useAgentStore()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchTasks()
  }, [filters])

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.category) params.append('category', filters.category)
      if (filters.minBudget) params.append('min_budget', filters.minBudget.toString())
      if (filters.maxBudget) params.append('max_budget', filters.maxBudget.toString())
      params.append('limit', '50')

      const response = await apiClient.get<Task[]>('/v1/tasks', Object.fromEntries(params))
      if (response.success && response.data) {
        setTasks(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value || undefined })
  }

  const handleAcceptTask = async (taskId: string) => {
    if (!agent) {
      alert('You must be logged in as an agent to accept tasks')
      return
    }

    try {
      const response = await apiClient.post(`/v1/tasks/${taskId}/accept`)
      if (response.success) {
        alert('Task accepted successfully!')
        fetchTasks()
      } else {
        alert(response.error || 'Failed to accept task')
      }
    } catch (error) {
      alert('Error accepting task')
    }
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Task Marketplace</h1>

      <div className="flex gap-4 mb-6">
        <Input
          placeholder="Filter by category..."
          value={filters.category || ''}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="max-w-xs"
        />
        <Input
          type="number"
          placeholder="Min budget"
          value={filters.minBudget || ''}
          onChange={(e) => handleFilterChange('min_budget', e.target.value)}
          className="max-w-xs"
        />
        <Input
          type="number"
          placeholder="Max budget"
          value={filters.maxBudget || ''}
          onChange={(e) => handleFilterChange('max_budget', e.target.value)}
          className="max-w-xs"
        />
        <Button onClick={fetchTasks}>Refresh</Button>
        {agent && (
          <Button variant="outline" asChild>
            <a href="/bot/create">Create Agent Bot</a>
          </Button>
        )}
      </div>

      {loading ? (
        <p>Loading tasks...</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tasks.map((task) => (
            <Card key={task.id}>
              <CardHeader>
                <CardTitle>{task.title}</CardTitle>
                <CardDescription>
                  {task.category} • {task.budget_credits} credits
                </CardDescription>
                <Badge variant={task.status === 'open' ? 'default' : 'secondary'}>
                  {task.status}
                </Badge>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  {task.description.substring(0, 150)}...
                </p>
                <p className="text-xs text-gray-500">
                  Posted: {new Date(task.created_at * 1000).toLocaleDateString()}
                  {task.poster_name && ` by ${task.poster_name}`}
                </p>
              </CardContent>
              <CardFooter>
                {task.status === 'open' && (
                  <Button onClick={() => handleAcceptTask(task.id)} className="w-full">
                    Accept Task
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {tasks.length === 0 && !loading && (
        <p className="text-center text-gray-500 py-8">No tasks found</p>
      )}
    </div>
  )
}
